import React, { useEffect, useMemo, useState } from "react";
import { X, Shield, AlertTriangle, ChevronRight, Trophy, Flame, Calendar, BarChart3 } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { fetchKamtibmas } from "../lib/api";
import { titleCase, formatNumber } from "../lib/format";

const CAT_COLORS = {
  kejahatan: "#ef4444",
  gangguan: "#f59e0b",
  bencana: "#a78bfa",
  pelanggaran: "#06b6d4",
};

const statusToColor = (s) => {
  const t = (s || "").toLowerCase();
  if (t === "kritis") return "#ef4444";
  if (t === "tinggi") return "#f59e0b";
  if (t === "sedang") return "#facc15";
  return "#10b981";
};

export const KamtibmasModal = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(2026);
  const [selectedProv, setSelectedProv] = useState(null);

  useEffect(() => {
    if (!open) return;
    fetchKamtibmas(year)
      .then((d) => { if (d?.success) setData(d); })
      .catch((err) => console.warn("Kamtibmas fetch failed:", err));
  }, [open, year]);

  const provincesSorted = useMemo(() => {
    if (!data?.provinces) return [];
    return [...data.provinces].sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [data]);

  const targetData = selectedProv
    ? data?.provinces?.find((p) => p.code === selectedProv)
    : null;

  const totalsByCat = useMemo(() => {
    const target = targetData || data;
    if (!target) return {};
    if (targetData) return target.categories || {};
    // National sum
    return data.provinces.reduce(
      (acc, p) => {
        Object.entries(p.categories || {}).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
        return acc;
      },
      {}
    );
  }, [data, targetData]);

  const totalAll = Object.values(totalsByCat).reduce((s, v) => s + (v || 0), 0);
  const pct = (n) => (totalAll ? ((n / totalAll) * 100).toFixed(1) : "0.0");

  const trends = data?.trends || [];
  const topPolda = provincesSorted[0];
  const peakDay = useMemo(() => {
    if (!trends.length) return null;
    return trends.reduce((m, t) => (t.total > (m?.total || 0) ? t : m), null);
  }, [trends]);

  if (!open) return null;

  return (
    <div className="nms-modal-backdrop full-modal" data-testid="kamtibmas-modal" onClick={onClose}>
      <div className="nms-modal kamtibmas-modal" onClick={(e) => e.stopPropagation()}>
        <button className="nms-modal-close" onClick={onClose} aria-label="Close" data-testid="kamtibmas-close">
          <X size={18} />
        </button>

        <div className="km-body">
          <aside className="km-left">
            <div className="km-brand">
              <div className="km-brand-icon"><Shield size={20} /></div>
              <div>
                <div className="km-brand-eyebrow">INTELLIGENCE</div>
                <div className="km-brand-title">KAMTIBMAS</div>
              </div>
            </div>

            <div className="km-total-card">
              <div className="kmt-lbl"><AlertTriangle size={12} /> TOTAL KEJADIAN</div>
              <div className="kmt-num">{formatNumber(data?.totalYearly || 0)}</div>
            </div>

            <button
              type="button"
              className={`km-region-card ${!selectedProv ? "active" : ""}`}
              onClick={() => setSelectedProv(null)}
              data-testid="km-region-nasional"
            >
              <div className="kmr-icon-circle">🌐</div>
              <div>
                <div className="kmr-name">NASIONAL</div>
                <div className="kmr-sub">Tinjauan Umum</div>
              </div>
            </button>

            <div className="km-data-period">
              <Calendar size={12} />
              <span>Data: {trends[0]?.date} s.d. {trends[trends.length - 1]?.date}</span>
            </div>
          </aside>

          <section className="km-center">
            <div className="km-center-head">
              <h2 className="km-center-title">{targetData ? titleCase(targetData.name) + " INCIDENT TRENDS" : "NASIONAL INCIDENT TRENDS"}</h2>
              <select className="km-year-select" value={year} onChange={(e) => setYear(+e.target.value)} data-testid="km-year-select">
                {(data?.availableYears || [2026, 2025, 2024, 2023]).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <div className="km-stat-row">
              {["kejahatan", "gangguan", "bencana", "pelanggaran"].map((cat) => (
                <div key={cat} className="km-stat-card" data-testid={`km-stat-${cat}`}>
                  <div className="km-stat-bar" style={{ background: CAT_COLORS[cat] }} />
                  <div className="km-stat-body">
                    <div className="kms-lbl">{cat.toUpperCase()}</div>
                    <div className="kms-val">{formatNumber(totalsByCat[cat] || 0)}</div>
                    <div className="kms-pct" style={{ color: CAT_COLORS[cat] }}>{pct(totalsByCat[cat] || 0)}%</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="km-chart-card">
              {trends.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trends}>
                    <defs>
                      {Object.entries(CAT_COLORS).map(([k, c]) => (
                        <linearGradient key={k} id={`g-${k}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={c} stopOpacity={0.7} />
                          <stop offset="100%" stopColor={c} stopOpacity={0.05} />
                        </linearGradient>
                      ))}
                    </defs>
                    <XAxis dataKey="date" stroke="#475569" tick={{ fontSize: 9, fill: "#94a3b8" }}
                      tickFormatter={(v) => v ? v.slice(5) : ""} />
                    <YAxis stroke="#475569" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <RTooltip contentStyle={{ background: "#0a1628", border: "1px solid #1e3a5f", borderRadius: 8, fontSize: 11 }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                    {Object.entries(CAT_COLORS).map(([k, c]) => (
                      <Area key={k} type="monotone" dataKey={k} stackId="1" stroke={c} fill={`url(#g-${k})`} fillOpacity={0.7} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="anal-loading">Memuat tren…</div>
              )}
            </div>

            <div className="km-bottom-grid">
              <div className="km-bot-card">
                <div className="kmb-lbl"><Trophy size={12} style={{ color: "#facc15" }} /> TOP POLDA</div>
                <div className="kmb-big">{topPolda ? titleCase(topPolda.name) : "—"}</div>
                <div className="kmb-sub">{topPolda ? `${formatNumber(topPolda.total)} kejadian` : ""}</div>
              </div>
              <div className="km-bot-card">
                <div className="kmb-lbl"><Flame size={12} style={{ color: "#ef4444" }} /> HARI PUNCAK</div>
                <div className="kmb-big">{peakDay?.date || "—"}</div>
                <div className="kmb-sub">{peakDay ? `${formatNumber(peakDay.total)} kejadian` : ""}</div>
              </div>
              <div className="km-bot-card">
                <div className="kmb-lbl"><Calendar size={12} style={{ color: "#06b6d4" }} /> PERIODE DATA</div>
                <div className="kmb-big">{trends[0]?.date} – {trends[trends.length - 1]?.date}</div>
                <div className="kmb-sub">{trends.length} hari</div>
              </div>
              <div className="km-bot-card">
                <div className="kmb-lbl"><BarChart3 size={12} style={{ color: "#a78bfa" }} /> TOTAL NASIONAL</div>
                <div className="kmb-big">{formatNumber(data?.totalYearly || 0)}</div>
                <div className="kmb-sub">tahun {year}</div>
              </div>
            </div>
          </section>

          <aside className="km-right">
            <div className="kmr-head">
              <BarChart3 size={14} style={{ color: "#06b6d4" }} />
              <span>INDEKS KERAWANAN PROVINSI</span>
            </div>
            <div className="kmr-legend">
              <span><i style={{ background: "#ef4444" }} /> KRITIS</span>
              <span><i style={{ background: "#f59e0b" }} /> TINGGI</span>
              <span><i style={{ background: "#facc15" }} /> SEDANG</span>
              <span><i style={{ background: "#10b981" }} /> RENDAH</span>
            </div>
            <ul className="kmr-list">
              {provincesSorted.map((p, i) => (
                <li key={p.code}>
                  <button
                    type="button"
                    className={`kmr-item ${selectedProv === p.code ? "active" : ""}`}
                    onClick={() => setSelectedProv(p.code === selectedProv ? null : p.code)}
                    data-testid={`km-prov-${p.code}`}
                  >
                    <span className="kmr-rank">#{i + 1}</span>
                    <div className="kmr-info">
                      <span className="kmr-pname">{titleCase(p.name)}</span>
                      <span className="kmr-pstatus" style={{ color: statusToColor(p.status) }}>{p.status}</span>
                    </div>
                    <span className="kmr-pval">{formatNumber(p.total)}</span>
                    <ChevronRight size={14} className="kmr-chev" />
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default KamtibmasModal;
